import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { UserProfile, FilterState, MapMode, SelectedElement, ActivityStatus, DisciplineType } from '@/types'

// =============================================================================
// AUTH SLICE
// =============================================================================

interface AuthSlice {
  user:       UserProfile | null
  isLoading:  boolean
  error:      string | null
  setUser:    (user: UserProfile | null) => void
  setLoading: (loading: boolean) => void
  setError:   (error: string | null) => void
  clearAuth:  () => void
}

// =============================================================================
// FILTER SLICE
// =============================================================================

interface FilterSlice {
  filters:         FilterState
  setParcelFilter: (parcels: number[]) => void
  setDiscipline:   (discipline: DisciplineType | null) => void
  setFamilyFilter: (familyCode: string | null) => void
  setSubcontractor:(companyId: string | null) => void
  setStatusFilter: (status: ActivityStatus | null) => void
  setDateRange:    (from: string | null, to: string | null) => void
  toggleGlobals:   () => void
  resetFilters:    () => void
}

// =============================================================================
// UI SLICE
// =============================================================================

interface UISlice {
  sidebarOpen:      boolean
  selectedElement:  SelectedElement | null
  mapMode:          MapMode
  activeTab:        string
  notifications:    number
  setSidebarOpen:   (open: boolean) => void
  toggleSidebar:    () => void
  setSelectedElement: (el: SelectedElement | null) => void
  setMapMode:       (mode: MapMode) => void
  setActiveTab:     (tab: string) => void
  setNotifications: (count: number) => void
}

// =============================================================================
// COMBINED STORE
// =============================================================================

type AppStore = AuthSlice & FilterSlice & UISlice

const DEFAULT_FILTERS: FilterState = {
  parcels:        [],
  discipline:     null,
  activityFamily: null,
  subcontractor:  null,
  status:         null,
  dateFrom:       null,
  dateTo:         null,
  showGlobals:    true,
}

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (set) => ({
        // ---------------------------------------------------------------------------
        // AUTH
        // ---------------------------------------------------------------------------
        user:       null,
        isLoading:  false,
        error:      null,

        setUser: (user) => set({ user }),
        setLoading: (isLoading) => set({ isLoading }),
        setError: (error) => set({ error }),
        clearAuth: () => set({ user: null, error: null }),

        // ---------------------------------------------------------------------------
        // FILTERS
        // ---------------------------------------------------------------------------
        filters: DEFAULT_FILTERS,

        setParcelFilter: (parcels) =>
          set((s) => ({ filters: { ...s.filters, parcels } })),

        setDiscipline: (discipline) =>
          set((s) => ({ filters: { ...s.filters, discipline } })),

        setFamilyFilter: (activityFamily) =>
          set((s) => ({ filters: { ...s.filters, activityFamily } })),

        setSubcontractor: (subcontractor) =>
          set((s) => ({ filters: { ...s.filters, subcontractor } })),

        setStatusFilter: (status) =>
          set((s) => ({ filters: { ...s.filters, status } })),

        setDateRange: (dateFrom, dateTo) =>
          set((s) => ({ filters: { ...s.filters, dateFrom, dateTo } })),

        toggleGlobals: () =>
          set((s) => ({ filters: { ...s.filters, showGlobals: !s.filters.showGlobals } })),

        resetFilters: () => set({ filters: DEFAULT_FILTERS }),

        // ---------------------------------------------------------------------------
        // UI
        // ---------------------------------------------------------------------------
        sidebarOpen:     true,
        selectedElement: null,
        mapMode:         'VIEW',
        activeTab:       'dashboard',
        notifications:   0,

        setSidebarOpen:     (sidebarOpen) => set({ sidebarOpen }),
        toggleSidebar:      () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
        setSelectedElement: (selectedElement) => set({ selectedElement }),
        setMapMode:         (mapMode) => set({ mapMode }),
        setActiveTab:       (activeTab) => set({ activeTab }),
        setNotifications:   (notifications) => set({ notifications }),
      }),
      {
        name:    'navabuena-epc-store',
        partialize: (state) => ({
          // Only persist filters and sidebar state; auth is managed by Supabase
          filters:     state.filters,
          sidebarOpen: state.sidebarOpen,
        }),
      }
    ),
    { name: 'NavabuenaStore' }
  )
)

// ---------------------------------------------------------------------------
// Selector hooks (for performance — avoids unnecessary re-renders)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Selector hooks — each subscribes to individual slices to avoid re-render
// loops caused by new object references. Functions in the Zustand store are
// stable (created once), so this pattern is safe and efficient.
// ---------------------------------------------------------------------------

export const useAuth = () => {
  const user       = useAppStore(s => s.user)
  const isLoading  = useAppStore(s => s.isLoading)
  const error      = useAppStore(s => s.error)
  const setUser    = useAppStore(s => s.setUser)
  const setLoading = useAppStore(s => s.setLoading)
  const setError   = useAppStore(s => s.setError)
  const clearAuth  = useAppStore(s => s.clearAuth)
  return { user, isLoading, error, setUser, setLoading, setError, clearAuth }
}

export const useFilters = () => {
  const filters          = useAppStore(s => s.filters)
  const setParcelFilter  = useAppStore(s => s.setParcelFilter)
  const setDiscipline    = useAppStore(s => s.setDiscipline)
  const setFamilyFilter  = useAppStore(s => s.setFamilyFilter)
  const setSubcontractor = useAppStore(s => s.setSubcontractor)
  const setStatusFilter  = useAppStore(s => s.setStatusFilter)
  const setDateRange     = useAppStore(s => s.setDateRange)
  const toggleGlobals    = useAppStore(s => s.toggleGlobals)
  const resetFilters     = useAppStore(s => s.resetFilters)
  return { filters, setParcelFilter, setDiscipline, setFamilyFilter, setSubcontractor, setStatusFilter, setDateRange, toggleGlobals, resetFilters }
}

export const useUI = () => {
  const sidebarOpen        = useAppStore(s => s.sidebarOpen)
  const selectedElement    = useAppStore(s => s.selectedElement)
  const mapMode            = useAppStore(s => s.mapMode)
  const activeTab          = useAppStore(s => s.activeTab)
  const notifications      = useAppStore(s => s.notifications)
  const setSidebarOpen     = useAppStore(s => s.setSidebarOpen)
  const toggleSidebar      = useAppStore(s => s.toggleSidebar)
  const setSelectedElement = useAppStore(s => s.setSelectedElement)
  const setMapMode         = useAppStore(s => s.setMapMode)
  const setActiveTab       = useAppStore(s => s.setActiveTab)
  const setNotifications   = useAppStore(s => s.setNotifications)
  return { sidebarOpen, selectedElement, mapMode, activeTab, notifications, setSidebarOpen, toggleSidebar, setSelectedElement, setMapMode, setActiveTab, setNotifications }
}
